import { useDispatch, useSelector } from 'react-redux';
import type { RootState } from '../store/store';
import type { OrgConfig, VisitorType } from '../types/orgConfig';
import {
    setVisitorinfo,
    setPreregisterId,
    setVisitorTypeId,
    setVisitorTypeInfo,
    setFlowInfoFlags,
    setIdValidationSkip,
    setVideoSkip,
    updateFormData,
    setCurrentStep,
    setSignatureImage,
    setPhotoImage,
    setDocumentSigned,
    setVideoUrl,
    setIsPrefill,
    setPreFillNew,
    setPreFillData,
    setRepeatCheckin,
    setPreRegisterData,
    setOrgTemplateIds,
    setWelcomeMessage,
    setWatchlistMessage,
    setVisitorFullName,
    setVisitStatus,
    setIdValidationFields,
    setOnSiteVisitors,
    resetVisitor,
    type FlowInfoFlags,
    type IdValidationFields,
    type RepeatCheckinContext,
    type IdentityModel,
    type OffenderModel,
} from '../store/slices/visitorSlice';
import type { OnSiteVisitor } from '../services/api/touchlessService';

import { useAppConfig } from './useAppConfig';
import type { VisitorTypeDocInfo } from '../types/visitorTypesResponse';
import { useCallback } from 'react';

export const useVisitorData = () => {
    const dispatch = useDispatch();
    const visitorState = useSelector((state: RootState) => state.visitor);
    const { config } = useAppConfig();

    const handleUpdatingLinkedValues= useCallback(( info: VisitorType | null) => {
        dispatch(setVisitorTypeInfo(info));
        dispatch(setFlowInfoFlags({
            isIdCapturingFlag: info?.isIdCapturingEnabled || false,
            visitorPhotoFlag: info?.photoFlag || false,
        }));
        if (info?.videoUrl) {
            dispatch(setVideoUrl(info.videoUrl));
        }
        if (info?.visitorTypeDocsInfos && info.visitorTypeDocsInfos.length > 0) {
            const templateIds = info.visitorTypeDocsInfos.map((x: VisitorTypeDocInfo) => x.orgDocTemplateId);
            dispatch(setOrgTemplateIds(templateIds));
        }
        if (info?.welcomeMessage) {
            dispatch(setWelcomeMessage(info.welcomeMessage.toString()));
        }
        if (info?.watchListHitMsg) {
            dispatch(setWatchlistMessage(info.watchListHitMsg.toString()));
        }
    }, [dispatch]);

    const refreshLinkedValuesFromConfig = useCallback(
        (cfg: OrgConfig | null, typeId: string | null) => {
            if (!cfg || !typeId) return;
            const vt = cfg.visitorType?.find((x) => x.id === typeId && x.status === 'ACTIVE');
            if (vt) handleUpdatingLinkedValues(vt);
        },
        [handleUpdatingLinkedValues],
    );

    return {
        ...visitorState,
        refreshLinkedValuesFromConfig,

        // Actions wrapped with dispatch
        updateVisitorFullName: (fullName: string) => dispatch(setVisitorFullName(fullName)),
        updateVisitorInfo: (info: Record<string, unknown> | null) => dispatch(setVisitorinfo(info)),
        updatePreregisterId: (id: string | null) => dispatch(setPreregisterId(id)),
        updateVisitorTypeId: (id: string | null) => {
            dispatch(setVisitorTypeId(id));
            // EN-4518: the validity window is configured per visitor type, so a prior
            // "skip the ID step" result is meaningless once the type changes. Reset to
            // false (show the step) and let the next submit re-check.
            //
            // Deliberately here and not in handleUpdatingLinkedValues: that helper also
            // runs on a language change (LanguageDropdown -> refreshLinkedValuesFromConfig),
            // which re-applies the translated config for the SAME visitor type. Resetting
            // there would make the ID step reappear if a visitor switched language after
            // the check had already resolved.
            dispatch(setIdValidationSkip(false));
            // EN-4854: the video reuse window is per visitor type too, same reasoning.
            dispatch(setVideoSkip(false));
            if (config?.visitorType) {
                const visitorType = config.visitorType.find((x) => x.id === id && x.status === "ACTIVE");
                handleUpdatingLinkedValues(visitorType || null);
            }
        },
        updateVisitorTypeInfo: (info: VisitorType | null) => {
            handleUpdatingLinkedValues(info);
        },
        updateFlowInfoFlags: (flags: FlowInfoFlags | null) => dispatch(setFlowInfoFlags(flags)),
        updateIdValidationSkip: (skip: boolean) => dispatch(setIdValidationSkip(skip)),
        updateVideoSkip: (skip: boolean) => dispatch(setVideoSkip(skip)),
        updatePhtotoFlag: (flag: boolean) => dispatch(setFlowInfoFlags({ isIdCapturingFlag: visitorState.flowInfoFlags?.isIdCapturingFlag || false, visitorPhotoFlag: flag })),
        updateIdCapturingFlag: (flag: boolean) => dispatch(setFlowInfoFlags({ isIdCapturingFlag: flag, visitorPhotoFlag: visitorState.flowInfoFlags?.visitorPhotoFlag || false })),
        updateForm: (data: Partial<typeof visitorState.formData>) => dispatch(updateFormData(data)),
        moveToStep: (step: string) => dispatch(setCurrentStep(step)),
        saveSignature: (image: string) => dispatch(setSignatureImage(image)),
        savePhoto: (image: string) => dispatch(setPhotoImage(image)),
        markDocumentSigned: (signed: boolean) => dispatch(setDocumentSigned(signed)),
        saveOrgTemplateIds: (ids: string[] | null) => dispatch(setOrgTemplateIds(ids)),
        updateIsPrefill: (isPrefill: boolean | null) => dispatch(setIsPrefill(isPrefill)),
        updatePreFillNew: (preFillNew: boolean | null) => dispatch(setPreFillNew(preFillNew)),
        updatePreFillData: (preFillData: Record<string, unknown> | null | undefined) => preFillData && dispatch(setPreFillData(preFillData)),
        // EN-4557: non-null marks a repeat cycle. It must POST with referenceVisitId, never PATCH.
        updateRepeatCheckin: (ctx: RepeatCheckinContext | null) => dispatch(setRepeatCheckin(ctx)),
        updatePreRegisterData: (preRegisterData: Record<string, unknown> | null) => dispatch(setPreRegisterData(preRegisterData)),
        updateVisitorVideoWatched: (watched: boolean) => watched && visitorState.visitorinfo && dispatch(setVisitorinfo({ ...visitorState.visitorinfo, videoWatched: visitorState.videoUrl })),
        updateWelcomeMessage: (message: string) => dispatch(setWelcomeMessage(message)),
        updateWatchlistMessage: (message: string) => dispatch(setWatchlistMessage(message)),
        updateVisitStatus: (status: { message: string | null; status: string | null; visitId: string | null; [ key: string ]: string | null | undefined; }) => dispatch(setVisitStatus(status)),
        updateIdValidationFields: (fields: IdValidationFields) => dispatch(setIdValidationFields(fields)),
        updateIdCaptureType: (type: string) => dispatch(setIdValidationFields({ ...visitorState.idValidationFields, idCaptureType: type })),
        updateIdValidatedModels: (models: IdentityModel[]) => dispatch(setIdValidationFields({ ...visitorState.idValidationFields, idValidatedModels: models })),
        updateOffenderValidatedModels: (models: OffenderModel[]) => dispatch(setIdValidationFields({ ...visitorState.idValidationFields, offenderValidatedModels: models })),
        updateIdAndOffenderValidatedModels: (identityModels: IdentityModel[], offenderModels: OffenderModel[]) => dispatch(setIdValidationFields({ ...visitorState.idValidationFields, idValidatedModels: identityModels, offenderValidatedModels: offenderModels })),
        saveVideoUrl: (url: string) => dispatch(setVideoUrl(url)),
        updateOnSiteVisitors: (visitors: OnSiteVisitor[] | null) => dispatch(setOnSiteVisitors(visitors)),
        clearVisitorData: () => dispatch(resetVisitor()),
    };
};
